import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import { BRAND } from "@/config";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/tours", label: t("nav.tours") },
    { to: "/experiencias", label: t("nav.experiences") },
    { to: "/nosotros", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contacto", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "hc-glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="Huitzil-Coatl" className="h-12 md:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase font-medium transition-colors hc-link-underline ${
                  isActive ? "text-[#C4A661]" : "text-[#F5F2EA] hover:text-[#C4A661]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="text-[12px] tracking-[0.2em] uppercase text-[#A3A095] hover:text-[#C4A661] transition-colors border border-[#1E2B25] px-3 py-1.5"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
          <Link to="/reservar" data-testid="nav-book-cta" className="hidden md:inline-flex hc-btn-primary !py-3 !px-5 !text-[11px]">
            {t("nav.book")}
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#F5F2EA] p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden hc-glass border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase py-2 ${isActive ? "text-[#C4A661]" : "text-[#F5F2EA]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/reservar" data-testid="nav-mobile-book" className="hc-btn-primary mt-2">
              {t("nav.book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
